/**
 * columnResizeMixin: drag-resize a column inside a fixed-width table.
 * Columns to the left stay as they are. The dragged column takes the new
 * width. Columns to the right share that same width change. The total
 * table width does not change, so data cannot overflow it.
 */
export const columnResizeMixin = {
	data() {
		return {
			columnWidths: {} as Record<string, number>,
			resizingColumn: null as any,
			resizeIndex: -1,
			resizeStartX: 0,
			resizeStartWidth: 0,
			resizeStartWidths: {} as Record<string, number>,
			didResize: false,
		};
	},
	computed: {
		tableResizeStyle(): any {
			return {
				tableLayout: 'fixed',
				width: '100%',
				maxWidth: '100%',
			};
		},
	},
	mounted() {
        const self = this as any;
        self._onColumnResizeBound = (event: MouseEvent) => self.onColumnResize(event);
        self._onStopColumnResizeBound = () => self.stopColumnResize();
        self.$nextTick(() => {
            self.snapshotColumnWidths(true);
            self.bindHeaderBodyScroll();
            // Tab panels in Dev can finish layout after the first paint; re-measure once settled.
            requestAnimationFrame(() => {
                self.snapshotColumnWidths(true);
            });
        });
        window.addEventListener('resize', self.clampWidthsToContainer);
    },
	beforeUnmount() {
		const self = this as any;
		self.stopColumnResize();
		self.unbindHeaderBodyScroll();
		window.removeEventListener('resize', self.clampWidthsToContainer);
	},
	methods: {
		getResizeColumnNames(): string[] {
			const self = this as any;
			if (self.columnNames) {
				return Array.from(self.columnNames || []) as string[];
			}
			return [];
		},
		minWidthForColumn(column: any) {
			if (column == 'actionButtonAtAudits' || column == 'deleteIcon') {
				return 48;
			}
			if (column == 'auditStatus' || column == 'findingStatus' || column == 'taskStatus' || column == 'statusCode') {
				return 144;
			}
			if (column == 'title' || column == 'Title' || column == 'controlName' || column == 'findingComment' || column == 'action') {
				return 140;
			}
			if (column == 'auditId' || column == 'taskId' || column == 'CID' || column == 'findingId' || column == 'TaskId') {
				return 88;
			}
			return 80;
		},
		columnStyle(column: any) {
			const self = this as any;
			const width = self.columnWidths[column];
			if (!width) {
				return {};
			}
			const names = Array.from(self.getResizeColumnNames() || []) as string[];
			const total = names.reduce((sum: number, name: string) => sum + (Number(self.columnWidths[name]) || 0), 0);
			if (!total) {
				return {};
			}
			const last = names[names.length - 1];
			let pct = (Number(width) / total) * 100;
			if (column === last) {
				const others = names.slice(0, -1).reduce((sum: number, name: string) => sum + (Number(self.columnWidths[name]) || 0), 0);
				pct = Math.max(0, 100 - (others / total) * 100);
			}
			return {
				width: pct + '%',
				minWidth: 0,
				maxWidth: pct + '%',
			};
		},
		snapshotColumnWidths(force = false) {
			const self = this as any;
			const table = self.$el?.querySelector?.('.table-header-table') as HTMLElement | null;
			const panel = table?.closest?.('.p-tabpanel') as HTMLElement | null;
			// Do not measure while the tab panel is hidden or the table has no layout yet.
			if (!table || self.getContainerWidth() <= 0) {
				return;
			}
			if (panel && (panel.hasAttribute('hidden') || panel.getAttribute('data-p-active') === 'false')) {
				return;
			}
			const headers =
				self.$el?.querySelectorAll?.(
					'.table-header-table thead tr.table-header-row th, .table-header-table thead tr:first-child th'
				) || [];
			const names = Array.from(self.getResizeColumnNames() || []) as string[];
			const next: Record<string, number> = force ? {} : { ...self.columnWidths };
			const seen = new Set<string>();
			headers.forEach((th: HTMLElement, index: number) => {
				const column = names[index];
				const liveWidth = Math.round(th.getBoundingClientRect().width);
				if (column && !seen.has(column) && (force || next[column] == null) && liveWidth > 0) {
					next[column] = Math.max(self.minWidthForColumn(column), liveWidth);
				}
				if (column) {
					seen.add(column);
				}
			});
			// Skip incomplete snapshots (e.g. all headers still 0px).
			const missing = names.filter((name: string) => next[name] == null);
			if (missing.length) {
				return;
			}
			self.columnWidths = next;
			self.clampWidthsToContainer();
		},
		getContainerWidth() {
			const self = this as any;
			const table = self.$el?.querySelector?.('.table-header-table') as HTMLElement | null;
			if (table && table.clientWidth) {
				return Math.max(60, Math.floor(table.clientWidth));
			}
			const wrap = self.$el?.querySelector?.('.table-header-wrap') as HTMLElement | null;
			if (wrap && wrap.clientWidth) {
				return Math.max(60, Math.floor(wrap.clientWidth));
			}
			const container =
				(self.$el?.querySelector?.('.table-container-fit') as HTMLElement | null) ||
				(self.$el?.querySelector?.('.table-container') as HTMLElement | null);
			if (container && container.clientWidth) {
				return Math.max(60, Math.floor(container.clientWidth));
			}
			return Math.max(60, Math.floor(self.$el?.clientWidth || window.innerWidth));
		},
		applyDeltaToRight(next: Record<string, number>, rightNames: string[], delta: number, source: Record<string, number>) {
			const self = this as any;
			if (!rightNames.length || !delta) {
				return;
			}
			if (delta > 0) {
				const slacks = rightNames.map((name: string) => {
					const minWidth = self.minWidthForColumn(name);
					const current = Number(source[name]) || minWidth;
					return Math.max(0, current - minWidth);
				});
				const slackSum = slacks.reduce((sum: number, value: number) => sum + value, 0);
				if (slackSum <= 0) {
					return;
				}
				let remaining = delta;
				rightNames.forEach((name: string, index: number) => {
					const minWidth = self.minWidthForColumn(name);
					const current = Number(source[name]) || minWidth;
					const steal = Math.min(slacks[index], (delta * slacks[index]) / slackSum);
					next[name] = current - steal;
					remaining -= steal;
				});
				for (let i = rightNames.length - 1; i >= 0 && remaining > 0.5; i--) {
					const name = rightNames[i];
					const minWidth = self.minWidthForColumn(name);
					const current = Number(next[name]) || minWidth;
					const slack = Math.max(0, current - minWidth);
					if (slack > 0) {
						const steal = Math.min(slack, remaining);
						next[name] = current - steal;
						remaining -= steal;
					}
				}
				return;
			}
			const extra = -delta;
			const weights = rightNames.map((name: string) => Number(source[name]) || self.minWidthForColumn(name));
			const weightSum = weights.reduce((sum: number, value: number) => sum + value, 0) || rightNames.length;
			rightNames.forEach((name: string, index: number) => {
				next[name] = weights[index] + extra * (weights[index] / weightSum);
			});
		},
		clampWidthsToContainer() {
			const self = this as any;
			const names = Array.from(self.getResizeColumnNames() || []) as string[];
			if (!names.length || !Object.keys(self.columnWidths || {}).length) {
				return;
			}
			const containerWidth = self.getContainerWidth();
			const next: Record<string, number> = { ...self.columnWidths };
			const total = names.reduce((sum: number, name: string) => sum + (Number(next[name]) || 0), 0);
			if (total > containerWidth) {
				let remaining = total - containerWidth;
				for (let i = names.length - 1; i >= 0 && remaining > 0; i--) {
					const name = names[i];
					const minWidth = self.minWidthForColumn(name);
					const current = Number(next[name]) || minWidth;
					const slack = Math.max(0, current - minWidth);
					if (slack > 0) {
						const steal = Math.min(slack, remaining);
						next[name] = current - steal;
						remaining -= steal;
					}
				}
				self.columnWidths = next;
			}
		},
		bindHeaderBodyScroll() {
			const self = this as any;
			self.unbindHeaderBodyScroll();
			const header = self.$el?.querySelector?.('.table-header-wrap') as HTMLElement | null;
			const body = self.$el?.querySelector?.('.table-body-scroll') as HTMLElement | null;
			if (!header || !body) {
				return;
			}
			self._onHeaderScroll = () => {
				if (body.scrollLeft !== header.scrollLeft) {
					body.scrollLeft = header.scrollLeft;
				}
			};
			self._onBodyScroll = () => {
				if (header.scrollLeft !== body.scrollLeft) {
					header.scrollLeft = body.scrollLeft;
				}
			};
			header.addEventListener('scroll', self._onHeaderScroll);
			body.addEventListener('scroll', self._onBodyScroll);
		},
		unbindHeaderBodyScroll() {
			const self = this as any;
			const header = self.$el?.querySelector?.('.table-header-wrap') as HTMLElement | null;
			const body = self.$el?.querySelector?.('.table-body-scroll') as HTMLElement | null;
			if (header && self._onHeaderScroll) {
				header.removeEventListener('scroll', self._onHeaderScroll);
			}
			if (body && self._onBodyScroll) {
				body.removeEventListener('scroll', self._onBodyScroll);
			}
			self._onHeaderScroll = null;
			self._onBodyScroll = null;
		},
		startColumnResize(event: MouseEvent, column: any) {
			const self = this as any;
			// Tab is visible by the time the user drags; always re-measure from live layout.
			self.snapshotColumnWidths(true);
			const th = (event.currentTarget as HTMLElement)?.closest('th');
			const liveWidth = Math.round(th?.getBoundingClientRect().width || 0);
			self._resizingTh?.classList.remove('is-resizing');
			self._resizingTh = th;
			th?.classList.add('is-resizing');
			const names = Array.from(self.getResizeColumnNames() || []) as string[];
			self.resizingColumn = column;
			self.resizeIndex = names.indexOf(column);
			self.resizeStartX = event.clientX;
			self.resizeStartWidth = liveWidth > 0 ? liveWidth : self.columnWidths[column] || self.minWidthForColumn(column);
			if (liveWidth > 0) {
				self.columnWidths = { ...self.columnWidths, [column]: liveWidth };
			}
			self.resizeStartWidths = { ...self.columnWidths };
			self.didResize = false;
			document.body.classList.add('col-resizing');
			document.addEventListener('mousemove', self._onColumnResizeBound);
			document.addEventListener('mouseup', self._onStopColumnResizeBound);
		},
		onColumnResize(event: MouseEvent) {
			const self = this as any;
			if (!self.resizingColumn || self.resizeIndex < 0) {
				return;
			}
			const delta = event.clientX - self.resizeStartX;
			if (Math.abs(delta) > 2) {
				self.didResize = true;
			}
			const names = Array.from(self.getResizeColumnNames() || []) as string[];
			const index = self.resizeIndex;
			const leftNames = names.slice(0, index);
			const rightNames = names.slice(index + 1);
			const minCol = self.minWidthForColumn(self.resizingColumn);
			const startWidth = Number(self.resizeStartWidth) || minCol;
			const rightSlack = rightNames.reduce((sum: number, name: string) => {
				const current = Number(self.resizeStartWidths[name]) || self.minWidthForColumn(name);
				return sum + Math.max(0, current - self.minWidthForColumn(name));
			}, 0);
			const nextWidth = Math.round(Math.min(startWidth + rightSlack, Math.max(minCol, startWidth + delta)));
			const applied = nextWidth - startWidth;
			const next: Record<string, number> = { ...self.resizeStartWidths };
			leftNames.forEach((name: string) => {
				next[name] = Number(self.resizeStartWidths[name]) || self.minWidthForColumn(name);
			});
			next[self.resizingColumn] = nextWidth;
			self.applyDeltaToRight(next, rightNames, applied, self.resizeStartWidths);
			self.columnWidths = next;
		},
		stopColumnResize() {
			const self = this as any;
			self._resizingTh?.classList.remove('is-resizing');
			self._resizingTh = null;
			self.resizingColumn = null;
			self.resizeIndex = -1;
			self.resizeStartWidths = {};
			document.body.classList.remove('col-resizing');
			document.removeEventListener('mousemove', self._onColumnResizeBound);
			document.removeEventListener('mouseup', self._onStopColumnResizeBound);
		},
		onHeaderClick(column: any) {
			const self = this as any;
			if (self.didResize) {
				self.didResize = false;
				return;
			}
			if (typeof self.sort === 'function') {
				self.sort(column);
			}
		},
	},
};
